// List of all socket event names (Enums)

export enum SocketEvents {
  // Sync Events (Business Process Automation)
  SOURCE_SYNC_COMPLETED = "source_sync:completed",
  /** A lead source's sync is failing in a way only a person can fix (token,
   *  app, permission, unreadable secret). Pushed to the tenant's Super Admins
   *  only — when first raised and at most once a day after. Drives the
   *  "Reconnect required" popup (SourceReconnectAlertHost). */
  SOURCE_RECONNECT_REQUIRED = "source:reconnect_required",
  /** The flagged source is healthy again (good sync, reconnect, disconnect). */
  SOURCE_RECONNECT_RESOLVED = "source:reconnect_resolved",

  // Lead Events
  NEW_LEAD = "lead:new",
  /** Dynamic call routing: a provider's call is ringing for a user / ended. */
  CALL_ROUTING_RINGING = "call_routing:ringing",
  CALL_ROUTING_ENDED = "call_routing:ended",
  /** Assignment added/removed — use instead of misusing NEW_LEAD for refetch */
  LEAD_ASSIGNMENT = "lead:assignment",
  LEAD_UPDATED = "lead:updated",
  LEAD_STATUS_UPDATED = "lead:status_updated",
  LEAD_BRANCH_TRANSFERRED = "lead:branch_transferred",
  LEAD_DELETED = "lead:deleted",
  /** A DEAL (child) was deleted — the parent lead survives. */
  CHILD_DELETED = "child:deleted",
  LEAD_RESTORED = "lead:restored",
  /** An existing lead re-enquired via a source — pushed only to its
   *  active assignees; drives the recapture popup modal. */
  LEAD_RECAPTURED = "lead:recaptured",

  /** Stage/status/substatus notification tone — server already resolved the
   *  tone + role targeting; the client coalesces bursts into one ring. */
  NOTIFICATION_TONE = "notification:tone",

  // Xale AI live voice call (server: src/ai/voice/live.socket.ts)
  AI_VOICE_READY = "ai:voice:ready",
  AI_VOICE_AUDIO = "ai:voice:audio",
  AI_VOICE_TRANSCRIPT = "ai:voice:transcript",
  AI_VOICE_STATE = "ai:voice:state",
  AI_VOICE_TOOL = "ai:voice:tool",
  /** A call recording the user asked to hear — the client plays it. */
  AI_VOICE_MEDIA = "ai:voice:media",
  /** A chart the model drew (`run_report` / `render_chart`) — `{ chart: AIChart }`;
   *  each push replaces the overlay's current chart card. */
  AI_VOICE_CHART = "ai:voice:chart",
  /** Server verdict on the OPEN recording — the `control_playback` tool or
   *  the spoken-stop fast path said `{ action: "stop" | "pause" | "resume" }`. */
  AI_VOICE_MEDIA_CONTROL = "ai:voice:media:control",
  /** Telecaller mode: the lead brief + objective for the overlay card, sent once the line is up. */
  AI_VOICE_TELECALLER = "ai:voice:telecaller",
  /** Telecaller mode: the outcome the AI logged (note / follow-up) — or the fallback transcript note. */
  AI_VOICE_OUTCOME = "ai:voice:outcome",
  /** The custom voice (Sarvam / ElevenLabs) failed mid-call — Gemini's own voice took over. */
  AI_VOICE_ENGINE = "ai:voice:engine",
  AI_VOICE_ERROR = "ai:voice:error",
  AI_VOICE_ENDED = "ai:voice:ended",

  /** A pending follow-up's date AND time just passed. Pushed only to the
   *  follow-up's personal-scope owners (assignees who hold the lead at
   *  personal level — not their managers or tenant admins), so anything that
   *  arrives here is the current user's to act on. Drives the "Follow-up Due"
   *  popup; the same alert reaches the mobile app as a push notification. */
  FOLLOWUP_DUE = "followup:due",

  /** A note was added on a lead or a deal. Pushed only to the note's
   *  personal audience (lead assignees + creator, plus the deal's assignees
   *  for a deal note; the author never gets it), gated by Settings → Manage
   *  → "Pop assigned users" → Notes. Drives the same interrupting popup as a
   *  chat attachment; clicking it opens the lead. Carries the note text, so
   *  nothing is fetched to show it. */
  NOTE_CREATED = "note:created",
  /** ALERTS: an admin/manager sent an alert to the roles under them —
   *  targeted at its recipients only. Adds the row to the Alert box and
   *  bumps the badge; `mode: "ANNOUNCE"` also pops the modal. */
  ALERT_NEW = "alert:new",
  /** ALERTS: my own read / read-all echoed to my other tabs (`unreadCount`),
   *  or a sender took an alert back (`action: "removed"`). */
  ALERT_UPDATED = "alert:updated",

  // FOLLOWUP_CHANGED ("followup:changed") REMOVED 2026-09-01 — the server no
  // longer emits it. A follow-up write refreshes only the ACTOR's own screen,
  // via `invalidateFollowUpDashboardQueries` on mutation success.

  /** An instalment on a deal's EMI plan needs attention: it falls due in N days
   *  (pre-reminder), it is due today, or it is overdue and being re-rung once
   *  per IST day. Same narrow audience as FOLLOWUP_DUE — the deal's
   *  personal-scope owners — because this is the owner's cue to go and collect,
   *  not a manager's dashboard. Drives the "Instalment Due" popup. The payload's
   *  `arm` says which of the three fired, and `reminderKey` is the dedupe key:
   *  the SAME instalment legitimately rings many times, so never dedupe on its
   *  id. */
  INSTALLMENT_DUE = "installment:due",

  // Razorpay Events
  /** A customer paid a payment link; the webhook already recorded the payment
   *  on the deal. Tenant-wide; carries lead / deal / who sent the link. */
  PAYMENT_LINK_PAID = "payment_link:paid",

  // Automation Events
  /** An automation run finished — refresh board + the affected lead's
   *  timeline + automation-failures banner. */
  AUTOMATION_RUN_FINISHED = "automation:run_finished",

  // Call Events (TeleCMI)
  INCOMING_CALL = "call:incoming",
  CALL_ENDED = "call:ended",

  // System Events
  NOTIFICATION = "system:notification",
  ERROR = "system:error",
  USER_ACCESS_REFRESH = "user:access_refresh",
  /** New SystemLog row — fan-out to tenant admins for the live audit feed. */
  SYSTEM_LOG_CREATED = "system_log:created",
  /** New activity for the tenant — content-free "refetch me" ping for the
   *  Recent Activity feed. Only delivered to clients that opted in via
   *  ACTIVITY_WATCH; throttled server-side to ~1 per tenant per 3s. */
  ACTIVITY_CREATED = "activity:created",

  // 🟢 WhatsApp Events (The New Nervous System)
  WHATSAPP_MESSAGE_NEW = "whatsapp:message:new", // Inbound msg -> Append to chat & Reorder sidebar
  WHATSAPP_MESSAGE_STATUS = "whatsapp:message:status", // Sent -> Delivered -> Read (Blue Ticks)
  WHATSAPP_CHAT_UPDATED = "whatsapp:chat:updated", // Unread count changed / Last message updated
  WHATSAPP_SESSION_EXPIRED = "whatsapp:session:expired", // 24h Window closed -> Disable input

  /** "Check-in / Check-out": my presence changed on the server (other tab /
   *  device, or the auto check-out sweep) — or, tenant-wide with
   *  `{ reason: "settings" }`, the org's settings changed. Re-read /me. */
  CHECK_IN_STATUS = "checkin:status",
  /** "Check-in / Check-out" team board: someone in the org changed presence —
   *  `{ userIds, status }`. Re-read /core/check-in/team. */
  CHECK_IN_TEAM = "checkin:team",
  /** "Check-in / Check-out": your working day has started — please check in. */
  CHECK_IN_REMINDER = "checkin:reminder",

  // Internal Chat Events
  INTERNAL_CHAT_NEW = "internal:chat:new",
  INTERNAL_CHAT_TYPING = "internal:chat:typing",
  INTERNAL_CHAT_READ = "internal:chat:read",
  INTERNAL_CHAT_DELETED = "internal:chat:deleted",
  /** Sender edited a text message — `{ messageId, body, mentions, editedAt }`. */
  INTERNAL_CHAT_EDITED = "internal:chat:edited",
}

/** Client → server: join / leave a lead's internal-chat LIVE room so pushes
 *  reach this client while the chat is open, member of the lead or not. */
export const INTERNAL_CHAT_WATCH = "internal:chat:watch";
export const INTERNAL_CHAT_UNWATCH = "internal:chat:unwatch";

/** Client → server: Xale AI live voice call control + mic audio (PCM16 16 kHz). */
export const AI_VOICE_START = "ai:voice:start";
export const AI_VOICE_AUDIO_IN = "ai:voice:audio";
export const AI_VOICE_TEXT_IN = "ai:voice:text";
export const AI_VOICE_STOP = "ai:voice:stop";
/** Client → server: the media card's playback state
 *  `{ active, playing, pausedBySpeech? }` — lets the server flush Gemini's
 *  cached audio while a recording plays and arm the spoken-stop fast path. */
export const AI_VOICE_MEDIA_STATE = "ai:voice:media:state";
